import React from "react";
import {
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Link,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import TruncatedText from "../../../common/TruncatedText";
import { networkOrganizationReportPath } from "../networkReportPaths";

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatSurveyPeriodDates = (startDate, endDate) => {
  const formattedDates = [startDate, endDate]
    .map(parseDate)
    .filter(Boolean)
    .map((date) => date.toLocaleDateString());

  return formattedDates.join(" – ");
};

export const getSurveyMetadata = (organization) => {
  const surveyName = organization.surveyName?.trim();
  const surveyPeriod = organization.surveyPeriodName?.trim() || formatSurveyPeriodDates(
    organization.surveyPeriodStartDate,
    organization.surveyPeriodEndDate
  );

  return [surveyName, surveyPeriod].filter(Boolean).join(" · ");
};

const uniqueFactors = (factors = []) => {
  const seen = new Set();

  return factors.filter((factor) => {
    const name = factor?.name?.trim();
    if (!name) return false;

    const key = name.toLocaleLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 2);
};

const FactorRows = ({ factors, tone, emptyText }) => {
  const visibleFactors = uniqueFactors(factors);
  const isStrength = tone === "strength";
  const rowBg = useColorModeValue(
    isStrength ? "green.50" : "red.50",
    isStrength ? "green.900" : "red.900"
  );
  const rowColor = useColorModeValue(
    isStrength ? "green.800" : "red.800",
    isStrength ? "green.100" : "red.100"
  );

  if (visibleFactors.length === 0) {
    return <Text color="gray.500" fontSize="sm">{emptyText}</Text>;
  }

  return (
    <Stack spacing={1}>
      {visibleFactors.map((factor) => (
        <Flex
          key={`${factor.id || "factor"}-${factor.name}`}
          align="start"
          gap={1}
          bg={rowBg}
          color={rowColor}
          borderRadius="sm"
          overflowWrap="anywhere"
          px={1.5}
          py={1}
          fontSize="xs"
          fontWeight="medium"
        >
          <Icon
            as={isStrength ? TriangleUpIcon : TriangleDownIcon}
            aria-hidden="true"
            boxSize={2.5}
            mt="3px"
            flexShrink={0}
          />
          <Text fontSize="inherit" lineHeight="short">{factor.name}</Text>
        </Flex>
      ))}
    </Stack>
  );
};

const NetworkOrganizationQuiltCard = ({ organization, networkId }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const subduedBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const hasNoSurveyData = ["not_started", "no_survey"].includes(organization.status);
  const reportPath = organization.reportAvailable && organization.organizationSurveyId
    ? networkOrganizationReportPath(networkId, organization.organizationSurveyId)
    : null;
  const surveyMetadata = getSurveyMetadata(organization);

  return (
    <Card
      bg={hasNoSurveyData ? subduedBg : cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      variant="outline"
    >
      <CardBody p={2.5}>
        <Stack spacing={2.5}>
          <Stack spacing={0.5} minW={0}>
            <Heading size="xs" overflowWrap="anywhere" lineHeight="short">
              {reportPath ? (
                <Link as={RouterLink} to={reportPath} color="inherit">
                  {organization.organizationName}
                </Link>
              ) : organization.organizationName}
            </Heading>
            {surveyMetadata && (
              <TruncatedText
                value={surveyMetadata}
                color={mutedColor}
                fontSize="xs"
                lineHeight="short"
                data-testid="survey-metadata"
              />
            )}
          </Stack>

          {hasNoSurveyData ? (
            <Text color={mutedColor} fontSize="sm" py={1}>No survey data yet</Text>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Text color={mutedColor} fontSize="xs" fontWeight="semibold" mb={1}>
                  Strengths
                </Text>
                <FactorRows
                  factors={organization.strengths}
                  tone="strength"
                  emptyText="No strengths available"
                />
              </Box>
              <Box>
                <Text color={mutedColor} fontSize="xs" fontWeight="semibold" mb={1}>
                  Development areas
                </Text>
                <FactorRows
                  factors={organization.developmentAreas}
                  tone="development"
                  emptyText="No development areas available"
                />
              </Box>
            </Stack>
          )}

        </Stack>
      </CardBody>
    </Card>
  );
};

export { FactorRows, uniqueFactors };
export default NetworkOrganizationQuiltCard;
